import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ArticleSource } from '../../model/article.model';
import { environment } from '../../../environments/environment';
import { Source } from '../../model/chat.model';

@Injectable({
  providedIn: 'root'
})
export class SiteLogoService {
  constructor(private http: HttpClient) {}

 /**
   * Attach logos to all sources in the articles array
   */
 addLogosToArticles(articles: any[]): Observable<any[]> {
  // Extract all sources from all articles
  const allSources = articles.flatMap(article => article.sources || []);

  // Extract unique domains
  const uniqueSites = Array.from(
    new Set(
      allSources
        .map(source => {
          try {
            return new URL(source.url).hostname.replace(/^www\./, '');
          } catch {
            return null; // skip invalid URLs
          }
        })
        .filter(Boolean) // remove nulls
    )
  );

  if (uniqueSites.length === 0) {
    return new Observable(observer => {
      observer.next(articles); // no domains to fetch
      observer.complete();
    });
  }

  // Call API with unique sites
  return this.http
    .post<{ results: { site: string; url: string }[] }>(
      environment.apiUrl + 'site-logo/signed-urls', // <-- your API endpoint
      { sites: uniqueSites }
    )
    .pipe(
      map(response => {
        // Build a map of site -> logo URL
        const logoMap = new Map<string, string>();
        response.results.forEach(item => {
          logoMap.set(item.site, item.url);
        });

        // Attach logos to each source in articles
        return articles.map(article => {
          const updatedSources = (article.sources || []).map((source: ArticleSource) => {
            const domain = new URL(source.url).hostname.replace(/^www\./, '');
            return {
              ...source,
              image: logoMap.get(domain) || 'assets/images/source-logos/default-logo.svg', // fallback
            };
          });

          return {
            ...article,
            sources: updatedSources,
          };
        });
      })
    );
}

/**
 * Refreshes logo URLs for a given list of sources.
 * Attaches fresh `image` URLs to each source.
 */
refreshLogosForSources(sources: Source[]): Observable<Source[]> {
  // Extract unique domains
  const uniqueSites = Array.from(
    new Set(
      sources
        .map(source => {
          try {
            return new URL(source.url).hostname.replace(/^www\./, '');
          } catch {
            return null; // skip bad URLs
          }
        })
        .filter(Boolean) // remove nulls
    )
  );

  if (uniqueSites.length === 0) {
    // No domains, just return sources as-is
    return new Observable(observer => {
      observer.next(sources);
      observer.complete();
    });
  }

  // Call API to fetch fresh logo URLs
  return this.http
    .post<{ results: { site: string; url: string }[] }>(
      environment.apiUrl + 'site-logo/signed-urls',
      { sites: uniqueSites }
    )
    .pipe(
      map(response => {
        // Build site -> logo map
        const logoMap = new Map<string, string>();
        response.results.forEach(item => {
          logoMap.set(item.site, item.url);
        });

        // Attach fresh logos to each source
        return sources.map(source => {
          const domain = new URL(source.url).hostname.replace(/^www\./, '');
          return {
            ...source,
            image: logoMap.get(domain) || 'assets/images/source-logos/default-logo.svg',
          };
        });
      })
    );
}


}
